"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import { Calendar, Clock, Truck, Key } from "lucide-react";
import { addDays, startOfDay, format } from "date-fns";

interface Slot {
  id: string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
}

interface DispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderPublicId: string;
  areaId: string;
  onConfirm: (slotId: string) => Promise<void>;
}

export function DispatchDialog({
  open,
  onOpenChange,
  orderId,
  orderPublicId,
  areaId,
  onConfirm,
}: DispatchDialogProps) {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    format(addDays(new Date(), 1), "yyyy-MM-dd")
  );
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [previewOtp, setPreviewOtp] = useState<string>("");

  const dateOptions = [
    { label: "Tomorrow", value: format(addDays(new Date(), 1), "yyyy-MM-dd") },
    { label: format(addDays(new Date(), 2), "EEE, MMM d"), value: format(addDays(new Date(), 2), "yyyy-MM-dd") },
    { label: format(addDays(new Date(), 3), "EEE, MMM d"), value: format(addDays(new Date(), 3), "yyyy-MM-dd") },
  ];

  useEffect(() => {
    if (open) {
      setPreviewOtp(String(Math.floor(1000 + Math.random() * 9000)));
      fetchSlots();
    }
  }, [open, selectedDate, areaId]);

  async function fetchSlots() {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/public/slots?type=DELIVERY&areaId=${areaId}&date=${selectedDate}`
      );
      if (response.ok) {
        const data = await response.json();
        setSlots(data.slots || []);
      }
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    if (!selectedSlot) return;

    setConfirming(true);
    try {
      await onConfirm(selectedSlot);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create delivery:", error);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Create Delivery for {orderPublicId}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Delivery Date
            </Label>
            <RadioGroup
              value={selectedDate}
              onValueChange={(v) => {
                setSelectedDate(v);
                setSelectedSlot("");
              }}
              className="grid grid-cols-3 gap-2"
            >
              {dateOptions.map((opt) => (
                <div key={opt.value}>
                  <RadioGroupItem
                    value={opt.value}
                    id={opt.value}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={opt.value}
                    className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                  >
                    <span className="text-sm font-medium">{opt.label}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time Slot
            </Label>

            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No delivery slots available for this date
              </p>
            ) : (
              <RadioGroup
                value={selectedSlot}
                onValueChange={setSelectedSlot}
                className="space-y-2"
              >
                {slots.map((slot) => {
                  const available = slot.capacity - slot.bookedCount;
                  const isFull = available <= 0;

                  return (
                    <div key={slot.id}>
                      <RadioGroupItem
                        value={slot.id}
                        id={slot.id}
                        disabled={isFull}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={slot.id}
                        className={`flex items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary ${
                          isFull ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <span className="font-medium">
                          {formatTimeRange(slot.startAt, slot.endAt)}
                        </span>
                        <Badge variant={isFull ? "secondary" : "outline"}>
                          {isFull ? "Full" : `${available} available`}
                        </Badge>
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            )}
          </div>

          {selectedSlot && (
            <Card className="bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Key className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Delivery OTP (for customer verification)
                    </p>
                    <p className="text-2xl font-bold tracking-widest text-primary">
                      {previewOtp}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedSlot || confirming}
          >
            {confirming ? "Creating..." : "Confirm Delivery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
